import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, Package, Shield, Clock, Phone, MapPin, CheckCircle2, Sparkles, UserCheck, Search, Globe, ShoppingBag, Droplets, Thermometer, Pill, Plane, Anchor, MapPinned, FileCheck, Lock } from 'lucide-react';
import { SHOP_URL } from '../lib/constants';

const HERO_IMAGE = "https://images.unsplash.com/photo-1576091358783-a212ec293ff3?w=800";
const BROOKLYN_IMAGE = "https://images.unsplash.com/photo-1635691332422-bb159df1ccc4?w=800";

export default function HomePage() {
  const titleRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section ref={titleRef} className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-cyan-50/30 to-blue-50/50 py-12 md:py-20">
        {/* Background decorations */}
        <div className="absolute top-20 right-0 w-96 h-96 bg-cyan-200/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-blue-200/20 rounded-full blur-3xl"></div>

        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Content */}
            <div className="flex flex-col gap-5">
              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-full text-sm font-semibold w-fit shadow-lg shadow-green-500/20">
                  <Truck className="w-4 h-4" />
                  FREE Delivery Across NYC
                  <Sparkles className="w-3 h-3" />
                </div>
                <a
                  href={SHOP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-slate-900 border border-slate-200 px-4 py-2 rounded-full text-sm font-semibold w-fit hover:bg-slate-50 transition-colors"
                  data-testid="hero-shop-badge"
                >
                  <ShoppingBag className="w-4 h-4 text-cyan-600" />
                  Now Ordering Online
                </a>
              </div>

              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                A Red Hook Original.{' '}
                <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">Your Health, Our History</span>{' '}
                For Over 20 Years.
              </h1>

              {/* Subtext */}
              <p className="text-lg text-slate-600 max-w-xl">
                Located at 376 Van Brunt Street, Nate's Pharmacy is more than just a drug store. We are a neighbor you can count on.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-3">
                <Link to="/transfer" className="bg-slate-900 text-white px-6 py-3 rounded-full font-medium hover:bg-slate-800 transition-colors flex items-center gap-2">
                  Transfer Prescription
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/refill" className="bg-white text-slate-900 px-6 py-3 rounded-full font-medium border border-slate-200 hover:bg-slate-50 transition-colors">
                  Refill Prescription
                </Link>
                <Link to="/delivery" className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-full font-medium hover:opacity-90 transition-opacity shadow-lg shadow-cyan-500/20 flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Free Delivery
                </Link>
                <a
                  href={SHOP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-slate-900 px-6 py-3 rounded-full font-medium border border-slate-200 hover:bg-slate-50 transition-colors flex items-center gap-2"
                  data-testid="hero-shop-online-button"
                >
                  <ShoppingBag className="w-4 h-4 text-cyan-600" />
                  Shop Online
                </a>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <div className="flex items-center gap-2 bg-white/80 px-3 py-1.5 rounded-full text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <span className="text-slate-700 font-medium">Since 2007</span>
                </div>
                <div className="flex items-center gap-2 bg-white/80 px-3 py-1.5 rounded-full text-sm">
                  <MapPin className="w-4 h-4 text-blue-600" />
                  <span className="text-slate-700 font-medium">Red Hook, Brooklyn</span>
                </div>
                <a href="tel:+17187970200" className="flex items-center gap-2 bg-slate-900 text-white px-3 py-1.5 rounded-full text-sm hover:bg-slate-800 transition-colors">
                  <Phone className="w-4 h-4" />
                  <span className="font-medium">(718) 797-0200</span>
                </a>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <div className="h-[350px] lg:h-[450px] rounded-3xl overflow-hidden shadow-2xl">
                <img src={HERO_IMAGE} alt="Pharmacist providing personalized care at Nate's Pharmacy" className="w-full h-full object-cover" />
              </div>
              {/* Floating card */}
              <div className="absolute -bottom-4 -left-4 bg-white p-4 rounded-2xl shadow-xl border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">Clinical Experts</p>
                    <p className="text-xs text-slate-500">Real Advocacy, Not Red Tape</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clinical Expertise Intro */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="inline-block text-xs font-semibold text-cyan-600 tracking-widest uppercase mb-3 bg-cyan-50 px-3 py-1 rounded-full">Why Nate's Is Different</span>
            <h2 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4">We Navigate the Complex Health Ecosystem For You</h2>
            <p className="text-slate-600 leading-relaxed">
              At Nate's, we don't just fill bottles; we navigate the complex health ecosystem for you. While others might tell you a claim is "rejected," our staff consists of clinical experts who use a specialized <strong className="text-slate-800">Tri-State Rejection Playbook</strong> to manually triage eligibility, NPI/NCPDP mismatches, and billing issues in real-time.
            </p>
          </div>
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-slate-50 to-cyan-50/40 p-6 md:p-8 rounded-2xl border border-slate-100">
              <p className="text-slate-600 leading-relaxed">
                Our team takes on the labor-intensive work that corporate chains find "too time-consuming." Whether it is our pharmacists managing the entire <strong className="text-slate-800">Prior Authorization process</strong> by coordinating clinical codes directly with your doctor, or our technicians providing organized <strong className="text-slate-800">Blister Packaging</strong> for complex regimens, we ensure your care is never interrupted by administrative red tape.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Direct Advocacy Features */}
      <section className="py-12 md:py-16 bg-slate-900">
        <div className="container-custom">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Healthcare Made Simple</h2>
            <p className="text-slate-400">Direct access to clinical experts who fight for your coverage.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Direct Pharmacist Advocacy - Featured */}
            <div className="md:row-span-2 bg-gradient-to-br from-cyan-500 to-blue-600 p-6 rounded-2xl flex flex-col justify-between min-h-[280px]">
              <div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-5">
                  <UserCheck className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Direct Pharmacist Advocacy</h3>
                <p className="text-cyan-100 text-sm">Skip the automated phone trees; speak directly to a Brooklyn pharmacist who provides personalized counseling and co-pay optimization in seconds.</p>
              </div>
              <Link to="/services" className="inline-flex items-center gap-2 text-white font-medium text-sm mt-4 hover:gap-3 transition-all">
                Our Services <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Active Troubleshooting */}
            <div className="md:col-span-2 bg-slate-800 p-5 rounded-2xl border border-slate-700">
              <div className="w-10 h-10 bg-violet-500/20 rounded-xl flex items-center justify-center mb-4">
                <Search className="w-5 h-5 text-violet-400" />
              </div>
              <h3 className="text-base font-semibold text-white mb-1">Active Troubleshooting</h3>
              <p className="text-slate-400 text-sm mb-3">Our billing specialists investigate rejections immediately to find solutions for coordination of benefits and plan exclusions.</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-slate-700 text-slate-300 text-xs rounded-full">Rejection Triage</span>
                <span className="px-3 py-1 bg-slate-700 text-slate-300 text-xs rounded-full">Benefits Coordination</span>
                <span className="px-3 py-1 bg-slate-700 text-slate-300 text-xs rounded-full">Prior Auth</span>
              </div>
            </div>

            {/* Multilingual Support */}
            <div className="bg-slate-800 p-5 rounded-2xl border border-slate-700">
              <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center mb-4">
                <Globe className="w-5 h-5 text-green-400" />
              </div>
              <h3 className="text-base font-semibold text-white mb-1">Multilingual Support</h3>
              <p className="text-slate-400 text-sm">Staff support in multiple languages, ensuring every patient is clearly understood.</p>
            </div>

            {/* Medication Management */}
            <div className="bg-slate-800 p-5 rounded-2xl border border-slate-700">
              <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center mb-4">
                <Package className="w-5 h-5 text-amber-400" />
              </div>
              <h3 className="text-base font-semibold text-white mb-1">Blister Packaging</h3>
              <p className="text-slate-400 text-sm">Organized medication packaging for complex regimens.</p>
            </div>
          </div>

          <div className="text-center mt-8">
            <Link to="/services" className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-medium transition-colors hover:gap-3">
              View All Services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* OTC Benefit Advocacy Hub */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-slate-50 via-white to-cyan-50/30">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Content */}
            <div>
              <span className="inline-block text-xs font-semibold text-emerald-600 tracking-widest uppercase mb-3 bg-emerald-50 px-3 py-1 rounded-full">OTC Benefits</span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">The OTC Benefit Advocacy Hub</h2>
              <p className="text-slate-600 mb-6">
                We don't just "swipe cards." We help you navigate the complex catalogs of OTC Benefit Programs to ensure you maximize your monthly allowance.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Shield className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900 mb-1">Active Benefit Management</h4>
                    <p className="text-slate-500 text-sm">We help you navigate complex OTC catalogs to maximize your monthly allowance.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                    <ShoppingBag className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900 mb-1">Benefit-Eligible Planograms</h4>
                    <p className="text-slate-500 text-sm">Aisles curated with health and wellness items covered by major local plans, reducing checkout frustration.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                    <UserCheck className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900 mb-1">Consultant-Led Shopping</h4>
                    <p className="text-slate-500 text-sm">Our staff acts as clinical advocates, identifying eligible immune support, vitamins, and daily essentials so you never leave benefits on the table.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Visual side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center mb-3">
                  <Pill className="w-6 h-6 text-emerald-600" />
                </div>
                <h4 className="text-sm font-semibold text-slate-900 mb-1">Vitamins</h4>
                <p className="text-xs text-slate-500">OTC eligible supplements</p>
              </div>
              <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-3">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="text-sm font-semibold text-slate-900 mb-1">Immune Support</h4>
                <p className="text-xs text-slate-500">Covered products</p>
              </div>
              <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center mb-3">
                  <Thermometer className="w-6 h-6 text-amber-600" />
                </div>
                <h4 className="text-sm font-semibold text-slate-900 mb-1">Health Tools</h4>
                <p className="text-xs text-slate-500">Monitors & testing kits</p>
              </div>
              <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 bg-violet-50 rounded-xl flex items-center justify-center mb-3">
                  <CheckCircle2 className="w-6 h-6 text-violet-600" />
                </div>
                <h4 className="text-sm font-semibold text-slate-900 mb-1">Daily Essentials</h4>
                <p className="text-xs text-slate-500">Personal care items</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Specialized Health & Beauty + Traveler Essentials */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container-custom">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-semibold text-cyan-600 tracking-widest uppercase mb-3 bg-cyan-50 px-3 py-1 rounded-full">Beyond the Counter</span>
            <h2 className="text-2xl md:text-4xl font-bold text-slate-900 mb-3">Specialized Inventory & Traveler Essentials</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">Premium products and curated solutions for Brooklyn's diverse community and visiting travelers.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Health & Beauty Card */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-6 md:p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-pink-500/20 rounded-xl flex items-center justify-center">
                  <Droplets className="w-5 h-5 text-pink-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Specialized Health & Beauty</h3>
              </div>
              <div className="space-y-4">
                <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                  <h4 className="text-sm font-semibold text-white mb-1">Professional-Grade Skincare</h4>
                  <p className="text-slate-400 text-sm">Premium dermatological brands focused on environmental protection for the Brooklyn climate.</p>
                </div>
                <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                  <h4 className="text-sm font-semibold text-white mb-1">Diagnostic Tools</h4>
                  <p className="text-slate-400 text-sm">High-accuracy blood pressure monitors, thermometers, and rapid testing kits for home health tracking.</p>
                </div>
                <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                  <h4 className="text-sm font-semibold text-white mb-1">Curated Personal Care</h4>
                  <p className="text-slate-400 text-sm">Trusted wellness products from high-tier supplements to daily hygiene essentials.</p>
                </div>
              </div>
            </div>

            {/* Traveler & Port Essentials Card */}
            <div className="bg-gradient-to-br from-cyan-600 to-blue-700 p-6 md:p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <Plane className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white">Traveler & Port Essentials</h3>
              </div>
              <div className="space-y-4">
                <div className="bg-white/10 p-4 rounded-xl border border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <Anchor className="w-4 h-4 text-cyan-200" />
                    <h4 className="text-sm font-semibold text-white">Last Stop Before the Port</h4>
                  </div>
                  <p className="text-cyan-100 text-sm">TSA-approved kits and port-ready toiletries for cruises or flights.</p>
                </div>
                <div className="bg-white/10 p-4 rounded-xl border border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <Pill className="w-4 h-4 text-cyan-200" />
                    <h4 className="text-sm font-semibold text-white">Motion & Sea Relief</h4>
                  </div>
                  <p className="text-cyan-100 text-sm">Specialized motion sickness solutions and portable first-aid kits for maritime and air travelers.</p>
                </div>
                <div className="bg-white/10 p-4 rounded-xl border border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPinned className="w-4 h-4 text-cyan-200" />
                    <h4 className="text-sm font-semibold text-white">Port-Proximity Service</h4>
                  </div>
                  <p className="text-cyan-100 text-sm">Quick, expert service for visiting tourists who need professional health advice before departure.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliant & Convenient Logistics */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-6 md:p-10 rounded-2xl border border-slate-100 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <span className="inline-block text-xs font-semibold text-green-600 tracking-widest uppercase mb-3 bg-green-50 px-3 py-1 rounded-full">Free Delivery</span>
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Compliant & Convenient Logistics</h2>
                  <p className="text-slate-600 text-sm leading-relaxed mb-5">
                    Your delivery service is built on safety and documentation, not just speed. Our free delivery in Brooklyn is backed by strict sig-log documentation standards for both controlled and non-controlled substances.
                  </p>
                  <Link to="/delivery" className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-full font-medium text-sm hover:bg-slate-800 transition-colors">
                    Request Delivery <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-xl">
                    <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileCheck className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">Sig-Log Documentation</p>
                      <p className="text-xs text-slate-500">Full compliance for all substances</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-xl">
                    <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Lock className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">Safe Handling</p>
                      <p className="text-xs text-slate-500">Professional care, pharmacy to door</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-xl">
                    <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Truck className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">Free Across Brooklyn</p>
                      <p className="text-xs text-slate-500">All 5 NYC boroughs covered</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <div className="h-[350px] rounded-2xl overflow-hidden shadow-xl">
                <img src={BROOKLYN_IMAGE} alt="Local neighborhood view of Red Hook Brooklyn" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-5 -right-5 bg-white p-5 rounded-xl shadow-xl border border-slate-100">
                <div className="text-4xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">20+</div>
                <div className="text-sm text-slate-500">Years Serving Brooklyn</div>
              </div>
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2">
              <span className="inline-block text-xs font-semibold text-cyan-600 tracking-widest uppercase mb-3 bg-cyan-50 px-3 py-1 rounded-full">Our Community</span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">More Than Medicine, We're Your Neighbor</h2>
              <p className="text-slate-600 mb-5">
                Since 2007, we've watched children grow up and families thrive in Brooklyn. We believe in genuine conversations. Your health is treated like our own.
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  </div>
                  <p className="text-slate-700 text-sm">Independent, locally owned</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-blue-600" />
                  </div>
                  <p className="text-slate-700 text-sm">Pharmacists who know your name</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-violet-600" />
                  </div>
                  <p className="text-slate-700 text-sm">Deep roots in Red Hook</p>
                </div>
              </div>
              <Link to="/our-story" className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-full font-medium text-sm hover:bg-slate-800 transition-colors">
                Learn Our Story <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-10 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h2 className="text-xl md:text-2xl font-semibold text-white mb-1">Ready to Switch to Better Care?</h2>
              <p className="text-slate-400 text-sm">Transfer your prescriptions today.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href={SHOP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-slate-900 px-6 py-2.5 rounded-full font-medium text-sm hover:bg-slate-100 transition-colors flex items-center gap-2"
                data-testid="cta-banner-shop-online-button"
              >
                <ShoppingBag className="w-4 h-4 text-cyan-600" />
                Shop Online
              </a>
              <Link to="/transfer" className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-2.5 rounded-full font-medium text-sm hover:opacity-90 transition-opacity">
                Transfer Now
              </Link>
              <a href="tel:+17187970200" className="border border-slate-600 text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-slate-700 transition-colors flex items-center gap-2">
                <Phone className="w-4 h-4" />
                (718) 797-0200
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
